import { WorkOrderDetailPage } from '@/features/work-orders/pages/work-order-detail-page'
import {
  woMaterialIssuesQuery,
  woProductionReportsQuery,
} from '@/features/work-orders/queries/phase12-queries'
import {
  cutPlanLinesQuery,
  issueLinesQuery,
  outputLinesQuery,
  projectDeficitsQuery,
  taskLinesQuery,
  workOrderQuery,
} from '@/features/work-orders/queries/work-order-detail-query'
import { messages } from '@/lib/i18n/messages'
import { createFileRoute } from '@tanstack/react-router'
import * as z from 'zod'

const paramsSchema = z.object({
  workOrderId: z.coerce.number().int().positive(),
})

/**
 * WO Detail route. Loader fetches the WO record first (so we can branch
 * on `work_order_type`) then prefetches every query the chosen variant
 * will consume. Cut-plan placements are deliberately NOT prefetched —
 * they're fetched lazily per-row inside `<CutPlanLineRow>` since the
 * loader can't know placement IDs ahead of time.
 *
 * `staticData.crumb` keeps the breadcrumb working while the WO record
 * itself is in-flight on first navigation; it's superseded by the
 * page-level title once the WO resolves.
 */
export const Route = createFileRoute('/projects/$projectId/_layout/work-orders/$workOrderId')({
  parseParams: paramsSchema.parse,
  stringifyParams: ({ workOrderId }) => ({ workOrderId: String(workOrderId) }),
  loader: async ({ params, context }) => {
    const qc = context.queryClient
    const wo = await qc.ensureQueryData(workOrderQuery(params.workOrderId))
    // Each ensureQueryData call is typed against its own query key, so we
    // launch them concurrently as named promises rather than try to push
    // heterogeneous queryOptions into a single array (TanStack Query's
    // generic-heavy types reject that mix even though the runtime is fine).
    const baseFetches: Promise<unknown>[] = [
      qc.ensureQueryData(taskLinesQuery(params.workOrderId)),
      qc.ensureQueryData(issueLinesQuery(params.workOrderId)),
      qc.ensureQueryData(outputLinesQuery(params.workOrderId)),
      qc.ensureQueryData(projectDeficitsQuery(wo.project_id)),
      qc.ensureQueryData(woMaterialIssuesQuery(wo.project_id, params.workOrderId)),
      qc.ensureQueryData(woProductionReportsQuery(wo.project_id, params.workOrderId)),
    ]
    // Cut-plan lines — лише cutter (welder не має розкрою).
    if (wo.work_order_type === 'cutter') {
      baseFetches.push(qc.ensureQueryData(cutPlanLinesQuery(params.workOrderId)))
    }
    await Promise.all(baseFetches)
    return { number: wo.number }
  },
  // Crumb: «<список> › <номер документа>» — the number from the loader, not the id.
  staticData: {
    crumbParent: (m: { params: { projectId: number } }) => ({
      label: messages.crumbs.workOrders,
      to: `/projects/${m.params.projectId}/work-orders/`,
    }),
    crumb: (m: { loaderData?: { number: string } }) => m.loaderData?.number ?? '…',
  },
  component: WorkOrderDetailRoute,
})

function WorkOrderDetailRoute() {
  const { workOrderId } = Route.useParams()
  return <WorkOrderDetailPage workOrderId={workOrderId} />
}
